import { get } from 'lodash';
import { resolve } from 'redux-optimist-promise';
import { apiDateToTimestamp } from 'Utils/date/apiDate';
import {
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  UPDATE_ATTENDEES,
} from 'Store/booking/proposals/constants';
import { FETCH_PREBOOKING_TRAVELERS } from 'Store/prebooking-form/prebookingform.action';
import { SIGNOUT } from 'Store/user-info/actions/signout';
import {
  TRIP_INFORMATION_UPDATE_ADULT,
  TRIP_INFORMATION_SET_ADULTS_NUMBER,
  DEFAULT_ADULT_VALUE,
  SET_ATTENDEES,
  SET_ADULTS,
} from './constants';
export type Adults = {
  readonly id?: string;
  readonly birthdate?: timestamp | null | undefined;
  readonly civility?: string | null | undefined;
  readonly firstName?: string | null | undefined;
  readonly lastName?: string | null | undefined;
  readonly unicodeFirstName?: string | null | undefined;
  readonly unicodeLastName?: string | null | undefined;
  readonly neolid?: string | null | undefined;
};
export type State = Array<Adults>;
const initialState: State = [];

function updateAdult(state: State, action: Action<any>): State {
  const { index, valuesToUpdate } = get(action, 'payload');

  if (!Number.isFinite(index)) {
    return state;
  }

  const adults = state || [];
  return Array(index >= adults.length ? index + 1 : adults.length)
    .fill({ ...DEFAULT_ADULT_VALUE })
    .map((defaultAdult, i) => {
      if (i !== index) {
        return adults[i] ? adults[i] : { ...DEFAULT_ADULT_VALUE };
      }

      return { ...{ ...(adults[i] ? adults[i] : defaultAdult) }, ...valuesToUpdate };
    });
}

function setAdultsNumber(state: State, action: Action<any>): State {
  const adultsNumber = get(action, 'payload.adultsNumber');

  if (adultsNumber === 0) {
    return [];
  }

  if (!Number.isFinite(adultsNumber)) {
    return state;
  }

  const adults = state || [];
  return Array(adultsNumber)
    .fill({ ...DEFAULT_ADULT_VALUE })
    .map((defaultAdult, i) => ({ ...(adults[i] ? adults[i] : defaultAdult) }));
}

const setAdults = (state: State, { payload }: Action<any>) => {
  if (!payload.adultsAttendees) {
    return state.map((adult) => ({
      neolid: adult.neolid,
    }));
  }

  return payload.adultsAttendees.map(
    (
      { birthdate, dateOfBirth, homePhoneNumber, mobilePhoneNumber, ...restAttendeeData },
      index,
    ) => {
      const birth = birthdate || dateOfBirth;
      return {
        ...restAttendeeData,
        birthdate: birth ? apiDateToTimestamp(birth) : null,
        phoneNumber: mobilePhoneNumber || homePhoneNumber,
        neolid: state?.[index]?.neolid,
      };
    },
  );
};

export function adultsReducer(state: State = initialState, action: Action<any>): State {
  switch (get(action, 'type')) {
    case TRIP_INFORMATION_UPDATE_ADULT:
      return updateAdult(state, action);

    case TRIP_INFORMATION_SET_ADULTS_NUMBER:
      return setAdultsNumber(state, action);

    case resolve(FETCH_PROPOSALS):
    case resolve(FETCH_PROPOSALS_BY_ID):
    case resolve(FETCH_PREBOOKING_TRAVELERS):
    case SET_ATTENDEES:
      return setAdults(state, action);

    case UPDATE_ATTENDEES: {
      if (!action.payload.attendees) {
        return state;
      }

      return state.map((adult) => {
        const updatedAdult = action.payload.attendees.find(({ id }) => id === adult.id) || {};
        return {
          ...adult,
          neolid: updatedAdult.customerId,
          gmNumber: updatedAdult.gmNumber,
          mainContactId: undefined,
        };
      });
    }

    case SET_ADULTS:
      return action.payload.map((adult, index) => ({
        ...state[index],
        ...adult,
        mainContactId: adult.neolid ? undefined : adult.mainContactId,
        neolid: adult.neolid || get(state[index], 'neolid'),
        ...(adult.address
          ? {
              address: {
                ...(get(state[index], 'address') as Record<string, unknown>),
                ...adult.address,
              },
            }
          : {}),
      }));

    case SIGNOUT:
      return initialState;

    default:
      return state;
  }
}
